package fyi.moko.hotpl.domain.service

import fyi.moko.hotpl.domain.model.GeoPoint
import fyi.moko.hotpl.domain.model.PlaceCategory
import fyi.moko.hotpl.domain.model.SearchPrediction
import fyi.moko.hotpl.domain.model.YellowPageEntry

/**
 * Platform-neutral contract for communicating with a place search provider.
 * Two implementations exist: [fyi.moko.hotpl.data.network.KtorPlaceSearchProvider]
 * (Google Places, paid) and [fyi.moko.hotpl.data.network.NominatimPlaceSearchProvider]
 * (OpenStreetMap Nominatim, free) — see `SharedModule.kt` for which one is wired in.
 */
interface PlaceSearchProvider {
    /**
     * Attribution text the UI must display alongside any results from this provider
     * (e.g. OSM's ODbL license requires "© OpenStreetMap contributors" wherever its
     * data is shown). Null when the provider has no such requirement.
     */
    val attributionText: String? get() = null

    /**
     * Calls the provider's autocomplete API and returns matching candidates.
     *
     * [origin] is the searching user's *exact* location. It does two jobs: it centres the
     * provider's location bias, and it is what makes
     * [fyi.moko.hotpl.domain.model.SearchPrediction.distanceMeters] available at all. Pass
     * null when the search should not be anchored to the user (an explicit destination
     * area) or when no location is known.
     *
     * 예전에는 여기에 지오해시 지역키(5자)를 넘겼는데, 그건 Firestore 캐시 파티션용 값이라
     * 셀 중심이 실제 위치에서 최대 3 km 넘게 어긋났다. 편향이 뭉개지는 것도 문제였지만
     * 결과에 거리를 붙일 수 없어 "가장 가까운 곳"을 위로 올릴 방법 자체가 없었다.
     */
    suspend fun searchAutocomplete(query: String, origin: GeoPoint? = null): List<SearchPrediction>

    /**
     * Whether this provider can answer a **query-less** "what is around me" browse —
     * [searchNearest] with a blank query.
     *
     * 화면의 "Near me"와 카테고리 칩이 정확히 그 호출이고, 못 하는 제공자에서는 그 칩들을
     * 아예 감춘다. 이 값이 없던 동안 웹(Nominatim 폴백)에서는 칩이 보이고 눌리기까지 하면서
     * 늘 빈 결과를 냈고, 화면은 그걸 **"주변에 없다"고 잘못 말했다** — 사실은 "이 제공자는
     * 그 질문에 답할 수 없다"였다.
     *
     * 플랫폼이 아니라 **제공자의 능력**에 묶은 이유: 조건이 "웹이냐"가 아니라 "지금 붙어 있는
     * 제공자가 할 수 있느냐"다. 웹에 Places 키를 넣으면 칩이 저절로 돌아오고, 반대로 키 없는
     * Android 빌드가 생기면 저절로 사라진다.
     */
    val supportsNearbyBrowse: Boolean get() = false

    /**
     * Distance-ranked search around [origin] — the "what is actually closest" path.
     *
     * Deliberately separate from [searchAutocomplete], which is a typeahead: that one ranks
     * by a blend of prominence and text match and returns only a handful of candidates, so
     * the nearest branch of a chain can be missing from its results outright no matter how
     * the request is biased (measured: a 460 m Starbucks absent while a 10 km one ranked
     * first). A blank [query] means "just show me what is around here".
     *
     * [category] narrows a query-less browse to one kind of place; with a query it is
     * ignored, since the query already says what is wanted. Costs more per call than
     * autocomplete, so callers should run it on an explicit user action — a submitted search, a "near me" tap — never per keystroke. Providers that
     * cannot rank by distance return an empty list rather than a prominence-ranked one
     * pretending to be nearest-first.
     */
    suspend fun searchNearest(
        query: String,
        origin: GeoPoint,
        category: PlaceCategory? = null,
    ): List<SearchPrediction> = emptyList()

    /** Resolves a specific provider place id into a full [YellowPageEntry]. */
    suspend fun fetchPlaceDetails(placeId: String): YellowPageEntry
}
